import React from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled, { css } from 'styled-components';
import { useSoFormStepsState } from '../../SoForm.context';
import moment from 'moment-timezone';
// import SoInternalRef from './SoInternalRef';
import { useTranslation } from 'react-i18next';

const getFormattedRangeFromDates = (dateFrom, dateTo) => {
  return `${moment(dateFrom).format('HH:mm')} - ${moment(dateTo).format(
    'HH:mm'
  )}`;
};

const getStopHeadingText = ({ address, isPickup, t }) => {
  const stopTypeText = isPickup
    ? t('confirmation.pickup_in')
    : t('confirmation.dropoff_in');
  const formattedText = `${stopTypeText} ${address.city}`;
  return formattedText;
};

const getStopContactText = ({ address }) => {
  const formattedText = `${address.address_line} ${address.zipcode} ${address.contact_name} ${address.contact_phone}`;
  return formattedText;
};

const getFormattedDateTextForTimeLine = ({ dateFrom, dateTo, isPickup, t }) => {
  const dateText = moment(dateFrom).format('D MMM YYYY');
  const stopTypeText = isPickup
    ? t('confirmation.pickup_of')
    : t('confirmation.dropoff_of');
  const hoursText = getFormattedRangeFromDates(dateFrom, dateTo);
  const formattedText = `${dateText}, ${stopTypeText} ${hoursText}`;
  return formattedText;
};

const TimeLineStop = ({ isPickup, quotePayload, isLast }) => {
  const { t } = useTranslation('Catalog');

  const shipment = quotePayload.shipments[0];

  const address = isPickup ? shipment.address_from : shipment.address_to;

  const dateFrom = isPickup
    ? shipment.pickup_time_from
    : shipment.dropoff_time_from;

  const dateTo = isPickup ? shipment.pickup_time_to : shipment.dropoff_time_to;

  const stopHeadingText = getStopHeadingText({
    address,
    isPickup,
    t,
  });
  const dateText = getFormattedDateTextForTimeLine({
    dateFrom,
    dateTo,
    isPickup,
    t,
  });
  const contactText = getStopContactText({
    address,
  });
  const pickupInstructions = shipment.pickup_comment_from_shipper_to_driver;
  const hasPickupInstructions = pickupInstructions.trim() !== '';
  const dropoffInstructions = shipment.dropoff_comment_from_shipper_to_driver;
  const hasDropoffInstructions = dropoffInstructions.trim() !== '';
  //   not showing instructions in summary for now
  const showInstructions =
    false &&
    ((isPickup && hasPickupInstructions) ||
      (!isPickup && hasDropoffInstructions));
  const instructionsValue = isPickup ? pickupInstructions : dropoffInstructions;

  const loads = shipment.loads;

  return (
    <TimeLineStopContainer isLast={isLast}>
      <IndicatorContainer>
        <IndicatorCircle>
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="16" cy="16" r="14" stroke="white" strokeWidth="4" />
            <circle cx="16" cy="16" r="12" fill="#CCD6E0" />
            <circle cx="16" cy="16" r="9" stroke="white" strokeWidth="2" />
            <circle cx="16" cy="16" r="8" fill="#CCD6E0" />
          </svg>
        </IndicatorCircle>
        {!isLast && (
          <IndicatorLine>
            <IndicatorLineRectangle />
          </IndicatorLine>
        )}
      </IndicatorContainer>
      <Stop isLast={isLast}>
        <Details>
          <StopInfo>
            <StopTitle>{stopHeadingText}</StopTitle>
            <StopDateAndTime>{dateText}</StopDateAndTime>
            <StopAddress>{contactText}</StopAddress>
          </StopInfo>
          <LoadsContainer>
            <LoadsInnerContainer>
              <StopTypeText>
                {isPickup
                  ? t('confirmation.pickup_of')
                  : t('confirmation.dropoff_of')}
                :
              </StopTypeText>
              {loads.map((load) => {
                const number_of_items = load.number_of_items;
                const isPlural = number_of_items > 1;
                const loadType = load.load_type;
                const palletType = load.pallet_type;
                let loadTypeText = null;

                if (loadType === 'PALLET' && palletType === 'EU_PALLET') {
                  loadTypeText = isPlural
                    ? t('confirmation.eu_pallets')
                    : t('confirmation.eu_pallet');
                } else if (
                  loadType === 'PALLET' &&
                  palletType === 'US_PALLET'
                ) {
                  loadTypeText = isPlural
                    ? t('confirmation.us_pallets')
                    : t('confirmation.us_pallet');
                } else if (
                  loadType === 'PALLET' &&
                  palletType === 'CUSTOM_PALLET'
                ) {
                  loadTypeText = isPlural
                    ? t('confirmation.custom_pallets')
                    : t('confirmation.custom_pallet');
                } else if (loadType === 'PARCEL') {
                  loadTypeText = isPlural
                    ? t('confirmation.boxes')
                    : t('confirmation.box');
                } else if (loadType === 'BULK') {
                  loadTypeText = isPlural
                    ? t('confirmation.bulks')
                    : t('confirmation.bulk');
                }
                return (
                  <>
                    <LoadRow>
                      <LoadRowText>
                        {number_of_items} {loadTypeText}
                      </LoadRowText>
                      <LoadSize>
                        <LoadSizeText>{load.item_height} cm</LoadSizeText>
                        <LoadSizeText>{load.total_weight} Kg</LoadSizeText>
                      </LoadSize>
                    </LoadRow>
                  </>
                );
              })}
            </LoadsInnerContainer>
          </LoadsContainer>

          {showInstructions && (
            <LoadsContainer>
              <LoadsInnerContainer>
                <StopTypeTextInsurance>
                  {t('confirmation.instructions_for_driver')}
                </StopTypeTextInsurance>
                <LoadRow>
                  <LoadRowTextInsurance>
                    {instructionsValue}
                  </LoadRowTextInsurance>
                </LoadRow>
              </LoadsInnerContainer>
            </LoadsContainer>
          )}
        </Details>
      </Stop>
    </TimeLineStopContainer>
  );
};

const TimeLineStopContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  ${(props) =>
    !!props.isLast &&
    css`
      margin-top: 16px;
    `}
`;

const IndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 16px;
  height: 100%;
`;

const IndicatorCircle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 4px;
  width: 24px;
  height: 24px;
  margin-bottom: auto;
`;

const IndicatorLine = styled.div`
  width: 2px;
  flex-grow: 1;
  position: relative;
`;

const IndicatorLineRectangle = styled.div`
  position: absolute;
  width: 2px;
  left: calc(50% - 2px / 2);
  top: 0px;
  bottom: 0px;

  /* grey / 200 */

  background: #e2e8ee;
`;

const Stop = styled.div`
  box-sizing: border-box;

  /* Auto layout */

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 0px 16px;
  gap: 16px;

  width: 512px;
  /* width: 100%; */

  /* grey / 200 */

  border-bottom: 1px solid #e2e8ee;
  ${(props) =>
    !!props.isLast &&
    css`
      border-bottom: none;
    `}
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;

  width: 512px;
`;

const StopInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;

  width: 512px;
  height: 80px;
`;
const StopTitle = styled.div`
  width: 512px;
  height: 24px;

  /* 20px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  /* identical to box height, or 120% */

  display: flex;
  align-items: center;

  /* grey / 700 */

  color: #54708c;
`;

const StopDateAndTime = styled.div`
  width: 512px;
  height: 24px;

  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 500 */

  color: #94a6b8;
`;

const StopAddress = styled.div`
  width: 512px;
  height: 24px;

  /* 16px/regular

Shipper App Default
*/
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 700 */

  color: #54708c;
`;

const LoadsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 12px 12px 16px;
  gap: 12px;

  width: 512px;
  /* height: 76px; */

  /* grey / 100 */

  background: #f2f5f8;
  /* grey / 300 */

  border-left: 4px solid #ccd6e0;
  border-radius: 4px;
`;
const LoadsInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;

  width: 484px;
  /* height: 52px; */
`;
const StopTypeText = styled.div`
  width: 93px;
  height: 24px;
  text-transform: capitalize;
  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 700 */

  color: #54708c;
`;

const StopTypeTextInsurance = styled.div`
  width: 100%;
  height: 24px;

  /* 16px/medium */

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  display: flex;
  align-items: center;

  /* grey / 700 */

  color: #54708c;
`;

const LoadRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;

  width: 484px;
  /* height: 24px; */
`;
const LoadRowText = styled.div`
  width: 300px;
  /* height: 24px; */

  /* 16px/regular

Shipper App Default
*/
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  /* grey / 700 */

  color: #54708c;
`;

const LoadRowTextInsurance = styled.div`
  width: 100%;
  /* height: 24px; */

  /* 16px/regular

Shipper App Default
*/
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  /* grey / 700 */

  color: #54708c;
  white-space: pre-wrap;
`;

const LoadSize = styled.div`
  /* Auto layout */

  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0px;
  gap: 8px;

  width: 168px;
  height: 24px;
`;
const LoadSizeText = styled.div`
  width: 80px;
  height: 24px;

  /* 16px/regular

Shipper App Default
*/
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  /* identical to box height, or 150% */

  /* grey / 700 */

  color: #54708c;
`;

const SoFormStopSummary = ({ className }) => {
  const stepsState = useSoFormStepsState();
  const quotePayload = stepsState.activeQuotePayload;

  return (
    <div className={className}>
      <TimeLineStop
        quotePayload={quotePayload}
        isPickup={true}
        key={'pickup'}
      />
      <TimeLineStop
        key={'dropoff'}
        quotePayload={quotePayload}
        isPickup={false}
        isLast={true}
      />
      {/* <SoInternalRef /> */}
    </div>
  );
};

SoFormStopSummary.propTypes = {
  className: PropTypes.string,
};

export default styled(SoFormStopSummary)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  width: 100%;
  /* secondary / white */

  background: #ffffff;
  border-radius: 4px;
`;
